import React, { useState, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SendOutlined } from '@ant-design/icons';
import {
  InputWrapper,
  TextAreaWrapper,
  StyledTextArea,
  CharCount,
  SendButton,
} from 'src/styled/Chat/ChatInput.styled';

interface IChatInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  maxLength?: number;
}

export const ChatInput: React.FC<IChatInputProps> = ({
  onSend,
  disabled = false,
  maxLength = 2000,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    const trimmedValue = value.trim();
    if (trimmedValue && !disabled) {
      onSend(trimmedValue);
      setValue('');

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter, new line on Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      setValue(newValue);

      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }
  };

  const isNearLimit = value.length > maxLength * 0.9;
  const canSend = value.trim().length > 0 && !disabled;

  return (
    <InputWrapper>
      <TextAreaWrapper>
        <StyledTextArea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('chat.placeholder')}
          disabled={disabled}
          rows={1}
          aria-label={t('chat.placeholder')}
        />
        {value.length > maxLength * 0.8 && (
          <CharCount isNearLimit={isNearLimit}>
            {value.length}/{maxLength}
          </CharCount>
        )}
      </TextAreaWrapper>
      <SendButton
        onClick={handleSend}
        disabled={!canSend}
        aria-label={t('chat.send')}
      >
        <SendOutlined />
      </SendButton>
    </InputWrapper>
  );
};

export default ChatInput;
